import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import {
  BookOpen,
  Briefcase,
  Building2,
  FileText,
  Globe,
  GraduationCap,
  Heart,
  Home,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Megaphone,
  Menu,
  PackageCheck,
  Plus,
  Receipt,
  Scale,
  Search,
  Send,
  Settings,
  ShoppingBag,
  Trophy,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react'

import { Logo } from '@/components/logo'
import { PostComposer } from '@/components/post-composer'
import { Dialog } from '@/components/ui/dialog'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { useLogout, useSession } from '@/features/auth/api'
import { useNotifications } from '@/features/notifications/api'
import { cn } from '@/lib/cn'
import type { Role } from '@/lib/types'

interface MenuItem {
  to: string
  label: string
  icon: ReactNode
  /** roles kosong berarti tersedia untuk semua peran. */
  roles?: Role[]
  section: string
}

/**
 * Semua yang bukan lima tujuan utama tinggal di menu ☰.
 *
 * Navigasi utama sengaja hanya lima: beranda, cari, tulis, aktivitas, dan
 * profil. Fitur kerja seperti proyek, sayembara, dan kontrak tetap ada dan
 * tetap satu klik jauhnya, tetapi tidak lagi berdesakan di depan orang yang
 * datang untuk membaca dan memamerkan karyanya.
 */
const menu: MenuItem[] = [
  { to: '/app/grup', label: 'Grup', icon: <Users className="size-4.5" />, section: 'Komunitas' },

  { to: '/app/kelas-saya', label: 'Kelas saya', icon: <GraduationCap className="size-4.5" />, section: 'Belajar & belanja' },
  { to: '/app/pesanan', label: 'Pesanan', icon: <Receipt className="size-4.5" />, section: 'Belajar & belanja' },
  { to: '/app/pesanan-jasa', label: 'Pesanan jasa', icon: <ShoppingBag className="size-4.5" />, section: 'Belajar & belanja' },

  { to: '/app/kelola-kelas', label: 'Kelola kelas', icon: <BookOpen className="size-4.5" />, roles: ['expert', 'super_admin'], section: 'Berjualan' },
  { to: '/app/jasa', label: 'Jasa saya', icon: <PackageCheck className="size-4.5" />, roles: ['expert', 'super_admin'], section: 'Berjualan' },
  { to: '/app/promosi-saya', label: 'Iklan saya', icon: <Megaphone className="size-4.5" />, section: 'Berjualan' },
  { to: '/app/dompet', label: 'Dompet', icon: <Wallet className="size-4.5" />, roles: ['expert', 'super_admin'], section: 'Berjualan' },

  { to: '/app/proyek', label: 'Proyek saya', icon: <Briefcase className="size-4.5" />, roles: ['client', 'super_admin'], section: 'Pekerjaan' },
  { to: '/app/proposal', label: 'Proposal saya', icon: <Send className="size-4.5" />, roles: ['expert', 'super_admin'], section: 'Pekerjaan' },
  { to: '/app/sayembara', label: 'Sayembara saya', icon: <Trophy className="size-4.5" />, roles: ['client', 'super_admin'], section: 'Pekerjaan' },
  { to: '/app/kontrak', label: 'Kontrak', icon: <FileText className="size-4.5" />, roles: ['client', 'expert', 'super_admin'], section: 'Pekerjaan' },
  { to: '/app/lamaran', label: 'Lamaran saya', icon: <Briefcase className="size-4.5" />, section: 'Pekerjaan' },
  { to: '/app/perusahaan', label: 'Perusahaan & lowongan', icon: <Building2 className="size-4.5" />, roles: ['employer', 'super_admin'], section: 'Pekerjaan' },

  { to: '/app/keuangan', label: 'Antrean pencairan', icon: <Wallet className="size-4.5" />, roles: ['finance', 'super_admin'], section: 'Internal' },
  { to: '/app/mediasi', label: 'Mediasi sengketa', icon: <Scale className="size-4.5" />, roles: ['mediator', 'super_admin'], section: 'Internal' },
  { to: '/app/promosi', label: 'Slideshow & iklan', icon: <Megaphone className="size-4.5" />, roles: ['admin_ops', 'super_admin'], section: 'Internal' },
  { to: '/app/pengguna', label: 'Pengguna', icon: <Users className="size-4.5" />, roles: ['admin_ops', 'super_admin'], section: 'Internal' },

  { to: '/app/ringkasan', label: 'Ringkasan', icon: <LayoutDashboard className="size-4.5" />, section: 'Lainnya' },
  { to: '/app/profil', label: 'Pengaturan akun', icon: <Settings className="size-4.5" />, section: 'Lainnya' },
  { to: '/panduan', label: 'Panduan', icon: <LifeBuoy className="size-4.5" />, section: 'Lainnya' },
  { to: '/', label: 'Situs publik', icon: <Globe className="size-4.5" />, section: 'Lainnya' },
]

/**
 * Kerangka aplikasi setelah masuk, dibentuk seperti Threads.
 *
 * Di layar lebar navigasinya lajur ikon tipis di kiri; di ponsel pindah ke
 * bawah, tempat ibu jari berada. Isinya satu kolom di tengah. Halaman yang
 * butuh ruang — penyunting kelas, tabel admin — tetap mendapat lebar penuh
 * karena kolom sempitnya diatur oleh halaman masing-masing, bukan di sini.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [menuTerbuka, setMenuTerbuka] = useState(false)
  const [tulisTerbuka, setTulisTerbuka] = useState(false)
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const tutupMenu = useCallback(() => setMenuTerbuka(false), [])

  // Menu ditutup setiap kali rute berganti; membiarkannya terbuka menutupi
  // halaman yang baru saja dibuka pengguna.
  useEffect(() => {
    setMenuTerbuka(false)
  }, [pathname])

  // Di ponsel halamannya putih penuh; di layar lebar latarnya keabuan dan
  // isinya berdiri di atas kartu, seperti Threads.
  return (
    <div className="min-h-full bg-surface md:bg-page">
      <a
        href="#konten"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-brand focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
      >
        Lewati ke konten utama
      </a>

      {/* Lajur kiri, layar lebar. */}
      <aside
        aria-label="Navigasi aplikasi"
        className="fixed inset-y-0 left-0 z-40 hidden w-20 flex-col items-center justify-between py-6 lg:flex"
      >
        <Link to="/app" aria-label="Beranda Skava" className="transition hover:scale-105">
          <Logo compact />
        </Link>

        <nav className="flex flex-col items-center gap-2">
          <TujuanUtama pathname={pathname} onTulis={() => setTulisTerbuka(true)} />
        </nav>

        <TombolMenu terbuka={menuTerbuka} onToggle={() => setMenuTerbuka((n) => !n)} />
      </aside>

      {/* Bilah atas, ponsel. */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between bg-surface/90 px-4 backdrop-blur-xl md:bg-page/90 lg:hidden">
        <span className="size-10" aria-hidden />
        <Link to="/app" aria-label="Beranda Skava">
          <Logo compact />
        </Link>
        <TombolMenu terbuka={menuTerbuka} onToggle={() => setMenuTerbuka((n) => !n)} />
      </header>

      <main id="konten" className="w-full px-4 pb-24 pt-4 sm:px-6 lg:pb-10 lg:pl-26 lg:pr-6 lg:pt-8">
        <div className="mx-auto w-full max-w-6xl">{children}</div>
      </main>

      {/* Bilah bawah, ponsel. */}
      <nav
        aria-label="Navigasi aplikasi"
        className="fixed inset-x-0 bottom-0 z-30 flex h-16 items-center justify-around border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl md:bg-page/95 lg:hidden"
      >
        <TujuanUtama pathname={pathname} onTulis={() => setTulisTerbuka(true)} />
      </nav>

      {menuTerbuka ? <PanelMenu pathname={pathname} onClose={tutupMenu} /> : null}

      <Dialog open={tulisTerbuka} onClose={() => setTulisTerbuka(false)} title="Kiriman baru">
        {/* Kotak tulis baru dipasang saat dialog terbuka supaya isinya
            kosong setiap kali dibuka, dan fokus langsung jatuh ke sana. */}
        {tulisTerbuka ? <PostComposer autoFocus onTerkirim={() => setTulisTerbuka(false)} /> : null}
      </Dialog>
    </div>
  )
}

/** Lima tujuan utama; dipakai lajur kiri dan bilah bawah. */
function TujuanUtama({ pathname, onTulis }: { pathname: string; onTulis: () => void }) {
  const { data: session } = useSession()
  const { data: notifications } = useNotifications(1, Boolean(session))
  const unread = notifications?.unread_count ?? 0
  const myId = session?.user.id

  const profilAktif = myId ? pathname === `/app/orang/${myId}` : false

  return (
    <>
      <TautanIkon to="/app" label="Beranda" aktif={pathname === '/app' || pathname === '/app/'}>
        <Home className="size-6" />
      </TautanIkon>

      <TautanIkon to="/app/cari" label="Cari" aktif={pathname.startsWith('/app/cari')}>
        <Search className="size-6" />
      </TautanIkon>

      <button
        type="button"
        onClick={onTulis}
        aria-label="Tulis kiriman"
        className="grid h-12 w-14 place-items-center rounded-[10px] bg-sunken text-faint transition hover:text-ink lg:h-12 lg:w-15"
      >
        <Plus className="size-6" />
      </button>

      <TautanIkon
        to="/app/notifikasi"
        label={unread > 0 ? `Aktivitas, ${unread} belum dibaca` : 'Aktivitas'}
        aktif={pathname.startsWith('/app/notifikasi')}
      >
        <span className="relative">
          <Heart className="size-6" />
          {unread > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full border-2 border-surface bg-danger" />
          ) : null}
        </span>
      </TautanIkon>

      {myId ? (
        <TautanIkon to={`/app/orang/${myId}`} label="Profil" aktif={profilAktif}>
          <UserRound className="size-6" />
        </TautanIkon>
      ) : null}
    </>
  )
}

function TautanIkon({
  to,
  label,
  aktif,
  children,
}: {
  to: string
  label: string
  aktif: boolean
  children: ReactNode
}) {
  return (
    <Link
      to={to}
      aria-label={label}
      title={label}
      aria-current={aktif ? 'page' : undefined}
      className={cn(
        'grid h-12 w-14 place-items-center rounded-[10px] transition lg:w-15',
        // Seperti Threads: tujuan yang aktif tidak diberi latar, cukup
        // ikonnya yang menghitam. Selebihnya pudar sampai disentuh.
        aktif ? 'text-ink [&_svg]:stroke-[2.5]' : 'text-faint hover:bg-sunken hover:text-ink',
      )}
    >
      {children}
    </Link>
  )
}

function TombolMenu({ terbuka, onToggle }: { terbuka: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label="Menu lainnya"
      aria-expanded={terbuka}
      aria-haspopup="menu"
      className={cn(
        'grid size-10 place-items-center rounded-xl transition lg:size-12',
        terbuka ? 'text-ink' : 'text-faint hover:text-ink',
      )}
    >
      <Menu className="size-6" />
    </button>
  )
}

/**
 * PanelMenu memuat seluruh fitur di luar lima tujuan utama.
 *
 * Di layar lebar ia muncul di samping tombolnya di pojok kiri bawah; di
 * ponsel ia membentang di bawah bilah atas.
 */
function PanelMenu({ pathname, onClose }: { pathname: string; onClose: () => void }) {
  const { data: session } = useSession()
  const logout = useLogout()
  const navigate = useNavigate()
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    panel.current?.focus()
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  const roles = session?.user.roles ?? []
  const items = menu.filter((item) => !item.roles || item.roles.some((role) => roles.includes(role)))
  const sections = [...new Set(items.map((item) => item.section))]

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden />

      <div
        ref={panel}
        tabIndex={-1}
        role="menu"
        aria-label="Menu lainnya"
        className={cn(
          'rise thin-scroll fixed z-50 overflow-y-auto rounded-2xl border border-line bg-raised p-2 shadow-[var(--shadow-lifted)] outline-none',
          'inset-x-3 top-16 max-h-[calc(100dvh-9rem)]',
          'lg:inset-x-auto lg:bottom-6 lg:left-22 lg:top-auto lg:max-h-[calc(100dvh-3rem)] lg:w-72',
        )}
      >
        {session ? (
          <div className="px-3 pb-2 pt-2">
            <p className="truncate text-sm font-bold text-ink">{session.user.full_name}</p>
            <p className="truncate text-xs text-muted">{session.user.email}</p>
          </div>
        ) : null}

        {sections.map((section) => (
          <div key={section} className="border-t border-line py-1.5 first:border-t-0">
            <p className="mono-label px-3 pb-1 pt-1.5 text-[10px] text-subtle">{section}</p>
            {items
              .filter((item) => item.section === section)
              .map((item) => {
                const active = item.to !== '/' && (pathname === item.to || pathname.startsWith(`${item.to}/`))
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    role="menuitem"
                    aria-current={active ? 'page' : undefined}
                    onClick={onClose}
                    className={cn(
                      'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition',
                      active ? 'bg-sunken text-ink' : 'text-ink hover:bg-sunken',
                    )}
                  >
                    <span className="text-muted">{item.icon}</span>
                    {item.label}
                  </Link>
                )
              })}
          </div>
        ))}

        <div className="flex items-center justify-between gap-2 border-t border-line px-3 py-2">
          <span className="text-sm font-semibold text-ink">Tampilan</span>
          <ThemeToggle />
        </div>

        <div className="border-t border-line pt-1.5">
          <button
            type="button"
            role="menuitem"
            disabled={logout.isPending}
            onClick={() =>
              logout.mutate(undefined, {
                onSuccess: () => navigate({ to: '/masuk' }),
              })
            }
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-danger transition hover:bg-danger-soft disabled:opacity-60"
          >
            <LogOut className="size-4.5" />
            {logout.isPending ? 'Keluar…' : 'Keluar'}
          </button>
        </div>
      </div>
    </>
  )
}
